import { useEffect, useRef } from "react";
import { cn } from "@/lib/cn";

/**
 * NaraOrb — the Siri-crimson liquid-glass orb (WebGL1 fragment shader), ported
 * verbatim from the standalone export. Fills its parent (absolute inset),
 * transparent outside the disc, so it drops into the round nav button.
 *
 * Single instance only (the center nav button) — one GL context. It pauses its
 * rAF loop when the tab is hidden, and tears the context down on unmount.
 * Falls back to a coral radial gradient if WebGL is unavailable.
 */
const VERT = "attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);}";

const FRAG = `precision highp float;
uniform vec2 uRes; uniform float uTime; uniform float uAmp;
uniform float uB1; uniform float uB2; uniform float uB3;
uniform float uListen; uniform float uForm; uniform float uDark; uniform float uThink;
uniform vec3 uA,uB,uC,uD;
float hash21(vec2 p){ p=fract(p*vec2(123.34,456.21)); p+=dot(p,p+45.32); return fract(p.x*p.y); }
float vnoise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(hash21(i),hash21(i+vec2(1.0,0.0)),u.x),
             mix(hash21(i+vec2(0.0,1.0)),hash21(i+vec2(1.0,1.0)),u.x),u.y); }
float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<5;i++){ v+=a*vnoise(p); p=p*2.02+vec2(31.0,17.0); a*=0.5; } return v; }
vec3 smile(vec2 uv,float t,float amp){
  float ends=smoothstep(0.52,0.12,abs(uv.x));
  float base=0.09+0.08*amp;
  float lo=base+0.30*uB1, md=base+0.30*uB2, hi=base+0.30*uB3;
  float th=mix(0.010,0.050,smoothstep(0.55,0.0,abs(uv.x)));
  float y0=lo*sin(uv.x*4.5+t*2.0);
  float y1=md*1.15*sin(uv.x*3.4-t*1.7+1.7);
  float y2=hi*0.85*sin(uv.x*5.2+t*2.3+3.4);
  float p0=exp(-pow((uv.y-y0)/(th*(1.0+0.8*uB1)),2.0));
  float p1=exp(-pow((uv.y-y1)/(th*(1.0+0.8*uB2)),2.0));
  float p2=exp(-pow((uv.y-y2)/(th*(1.0+0.8*uB3)),2.0));
  vec3 cA=vec3(0.50,0.12,0.10), cB=vec3(0.86,0.23,0.15), cC=vec3(0.90,0.38,0.23);
  return max(max(cA*p0, cB*p1), cC*p2)*ends;
}
vec3 listenBall(vec2 p,float t){
  float ang=1.5708-t*1.8; float R=0.40;
  vec2 head=vec2(cos(ang),sin(ang))*R;
  float ball=exp(-pow(length(p-head)/(0.075+0.06*uAmp),2.0));
  float rr=length(p), pa=atan(p.y,p.x);
  float ring=exp(-pow((rr-R)/0.05,2.0));
  float da=mod(pa-ang+6.2831853,6.2831853);
  float trail=ring*exp(-da*1.5);
  vec3 col=mix(vec3(0.60,0.14,0.11),vec3(0.95,0.34,0.20),0.5+0.5*sin(pa*1.5+t));
  col=mix(col,vec3(0.98,0.50,0.30),0.5+0.5*cos(pa*1.2-t*0.7));
  return col*(ball*1.4+trail*0.7);
}
vec3 wave(vec2 p){ return mix(smile(p,uTime,0.14+uAmp), listenBall(p,uTime), uListen); }
vec3 aurora(vec2 p){
  float t=uTime;
  float w=fbm(p*1.3+vec2(t*0.07,-t*0.04));
  vec2 q=p+0.45*vec2(fbm(p*1.1+t*0.09+w), fbm(p*1.1-t*0.08-w));
  float n1=fbm(q*1.7+t*0.05);
  float n2=fbm(q*2.4-t*0.07+3.0);
  float pa=atan(p.y,p.x);
  float swirl=0.5+0.5*sin(pa*3.0+length(p)*4.0-t*1.2+n1*3.0);
  vec3 c=mix(uA,uB,clamp(0.45+1.1*(n1-0.5),0.0,1.0));
  c=mix(c,uC,smoothstep(0.35,0.95,n2));
  c+=uD*pow(max(0.0,n1-n2),1.4)*0.7;
  c=mix(c, c*1.3, swirl*0.5);
  float g=smoothstep(1.06,0.05,length(p));
  c*=(0.42+0.72*g)*(1.0+uAmp*0.7);
  return c*1.15;
}
vec3 nebula(vec2 p){
  float t=uTime*0.04;
  float n=fbm(p*1.4+vec2(t,t*0.5));
  float n2=fbm(p*2.8-vec2(t*0.7,0.0)+n*1.5);
  float dust=fbm(p*5.0+n2);
  vec3 c=vec3(0.05,0.02,0.11);
  c=mix(c, vec3(0.55,0.12,0.55), smoothstep(0.35,0.85,n));
  c=mix(c, vec3(0.12,0.18,0.68), smoothstep(0.45,0.92,n2));
  c+=vec3(0.95,0.55,0.75)*pow(max(0.0,dust-0.55),2.0)*1.3;
  c*=0.8+0.5*uAmp;
  vec2 g=floor(p*60.0); float h=fract(sin(dot(g,vec2(12.9,78.2)))*43758.5);
  c+=vec3(1.0,0.96,0.9)*step(0.992,h)*pow(fract(h*97.0),10.0)*2.2;
  return c;
}
vec3 siriPal(float h){
  vec3 c0=vec3(0.737,0.510,0.953); vec3 c1=vec3(0.961,0.725,0.918);
  vec3 c2=vec3(0.553,0.624,1.000); vec3 c3=vec3(0.667,0.431,0.933);
  vec3 c4=vec3(1.000,0.404,0.471); vec3 c5=vec3(1.000,0.729,0.443);
  vec3 c6=vec3(0.776,0.525,1.000);
  float s=h*7.0; float k=smoothstep(0.0,1.0,fract(s));
  if(s<1.0) return mix(c0,c1,k);
  if(s<2.0) return mix(c1,c2,k);
  if(s<3.0) return mix(c2,c3,k);
  if(s<4.0) return mix(c3,c4,k);
  if(s<5.0) return mix(c4,c5,k);
  if(s<6.0) return mix(c5,c6,k);
  return mix(c6,c0,k);
}
vec3 siriPalCrimson(float h){
  vec3 c0=vec3(0.478,0.118,0.102); vec3 c1=vec3(0.682,0.204,0.165);
  vec3 c2=vec3(0.851,0.267,0.212); vec3 c3=vec3(0.949,0.447,0.306);
  vec3 c4=vec3(0.957,0.663,0.361); vec3 c5=vec3(0.780,0.231,0.369);
  vec3 c6=vec3(0.925,0.549,0.549);
  float s=h*7.0; float k=smoothstep(0.0,1.0,fract(s));
  if(s<1.0) return mix(c0,c1,k);
  if(s<2.0) return mix(c1,c2,k);
  if(s<3.0) return mix(c2,c3,k);
  if(s<4.0) return mix(c3,c4,k);
  if(s<5.0) return mix(c4,c5,k);
  if(s<6.0) return mix(c5,c6,k);
  return mix(c6,c0,k);
}
float bellAtt(float x){ float a=4.0; return pow(a/(a+x*x), a); }
vec3 siriWave(vec2 uv){
  float t=uTime;
  vec3 sharp=vec3(0.0);
  vec3 glow=vec3(0.0);
  for(int k=0;k<7;k++){
    float fk=float(k);
    float s=hash21(vec2(fk*1.37+0.5, 3.1));
    float spd=0.7+s*0.9;
    float wid=0.9+s*1.3;
    float band=(k<3)?uB1:(k<5)?uB2:uB3;
    float amp=(0.14+0.20*s)*(0.45+1.4*band+0.5*uAmp);
    amp*=0.55+0.45*sin(t*(0.5+s*0.8)+fk*2.3);
    amp+=uThink*(0.10+0.07*s)*(0.55+0.45*sin(t*2.4 - fk*0.9));
    float x=uv.x/wid;
    float env=bellAtt(uv.x*1.6);
    float yoff=(fk-3.0)*0.058*env;
    float y=yoff + amp*sin(x*4.6 - t*spd*(1.0+uThink*0.6) - fk*1.1)*bellAtt(uv.x*2.1);
    float thS=mix(0.010,0.040,env);
    float thB=thS*4.5;
    vec3 c=(uForm>3.5)?siriPalCrimson(fk/7.0):siriPal(fk/7.0);
    sharp=max(sharp, c*exp(-pow((uv.y-y)/thS,2.0)));
    glow += c*exp(-pow((uv.y-y)/thB,2.0))*0.16;
  }
  float sweep = 1.0 + uThink*0.7*exp(-pow((uv.x-(fract(t*0.30)*2.4-1.2))/0.30, 2.0));
  return (sharp + glow) * sweep;
}
vec3 scene(vec2 p){
  if(uForm<0.5) return wave(p);
  if(uForm<1.5) return aurora(p);
  if(uForm<2.5) return nebula(p);
  return siriWave(p);
}
const float uRadius=1.0;
const float uDistort=1.30;
const float uDispersion=1.70;
float sdCircle(vec2 p,float r){ return length(p)-r; }
float getDist(vec2 p){
  float sd=sdCircle(p,uRadius);
  float md=length(p);
  float fall=max(smoothstep(0.0,0.8,md),0.06);
  float tweak=mix(0.02/fall,0.10/fall,uDistort*sd);
  tweak=min(-tweak,0.0);
  return sd-tweak;
}
vec3 refrakt(float sd, vec2 st, vec3 bg, vec2 ouv){
  vec2 offset=mix(vec2(0.0), normalize(st)/sd, length(st));
  float disp=uDispersion*0.01;
  vec2 rO=offset*disp*1.2, gO=offset*disp, bO=offset*disp*0.8;
  float r=scene(ouv+rO).r, g=scene(ouv+gO).g, b=scene(ouv+bO).b;
  float op=smoothstep(0.0,0.0025,-sd);
  return mix(bg, vec3(r,g,b), op);
}
void main(){
  vec2 uv=(gl_FragCoord.xy*2.0-uRes)/uRes.y;
  float r=length(uv);
  if(r>1.04){gl_FragColor=vec4(0.0);return;}
  vec3 bg=scene(uv);
  float e=0.0009;
  vec3 col=refrakt(getDist(uv),uv,bg,uv)
          +refrakt(getDist(uv+vec2(e,0.0)),uv+vec2(e,0.0),bg,uv)
          +refrakt(getDist(uv-vec2(e,0.0)),uv-vec2(e,0.0),bg,uv)
          +refrakt(getDist(uv+vec2(0.0,e)),uv+vec2(0.0,e),bg,uv)
          +refrakt(getDist(uv-vec2(0.0,e)),uv-vec2(0.0,e),bg,uv);
  col*=0.2;
  float z=sqrt(max(0.0,1.0-r*r));
  float fres=pow(1.0-z,3.0);
  float lum=max(max(col.r,col.g),col.b);
  float disc=smoothstep(1.04,0.99,r);
  vec3 baseTint = mix(vec3(0.92,0.93,0.97), vec3(0.055,0.065,0.125), uDark);
  bool filled = (uForm>0.5 && uForm<2.5);
  float matA = filled ? 0.86 : 0.42;
  vec3 prem = (baseTint*matA + col) * disc;
  prem += vec3(1.0,1.0,1.04) * smoothstep(0.93,0.995,r)*smoothstep(1.0,0.985,r) * (0.5 - 0.3*uDark) * disc;
  vec3 N = normalize(vec3(uv, z + 0.35));
  float spec = pow(max(0.0, dot(N, normalize(vec3(-0.30,0.52,0.80)))), 22.0);
  prem += vec3(1.0,1.0,1.03) * spec * 0.12 * disc;
  float L = max(max(prem.r, prem.g), prem.b);
  prem /= max(1.0, L);
  float a = clamp(matA + lum*1.6 + fres*0.30, 0.0, 1.0) * disc;
  gl_FragColor = vec4(prem, a);
}`;

export function NaraOrb({
  className,
  /** WebGL drawing-buffer resolution (square). CSS scales it to the parent. */
  res = 160,
  /** 0..1 self-driven "thinking" motion when idle. */
  think = 0.35,
}: {
  className?: string;
  res?: number;
  think?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    cv.width = res;
    cv.height = res;
    // Whenever the live shader can't run (no WebGL, or a driver/headless backend
    // that can't compile it, or it produces no output), HIDE the canvas so the
    // crimson base disc rendered behind it (by the nav button) shows — never a
    // blank/white/broken button. A live-but-uninitialized WebGL buffer composites
    // as opaque white in some backends, so removing the canvas is the safe move.
    const applyFallback = () => {
      cv.style.display = "none";
    };

    let gl: WebGLRenderingContext | null = null;
    try {
      gl =
        (cv.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: true }) as
          | WebGLRenderingContext
          | null) ||
        (cv.getContext("experimental-webgl", { premultipliedAlpha: true, alpha: true }) as
          | WebGLRenderingContext
          | null);
    } catch {
      gl = null;
    }
    if (!gl) {
      applyFallback();
      return;
    }

    let compileOk = true;
    const compile = (type: number, src: string) => {
      const o = gl!.createShader(type)!;
      gl!.shaderSource(o, src);
      gl!.compileShader(o);
      if (!gl!.getShaderParameter(o, gl!.COMPILE_STATUS)) {
        compileOk = false;
        console.warn("NaraOrb: shader compile failed —", gl!.getShaderInfoLog(o));
      }
      return o;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!compileOk || !gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      // Shader couldn't build on this GPU/backend → graceful crimson fallback.
      applyFallback();
      return;
    }
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const lp = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(lp);
    gl.vertexAttribPointer(lp, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const U = (n: string) => gl.getUniformLocation(prog, n);
    const u: Record<string, WebGLUniformLocation | null> = {};
    ["uRes", "uTime", "uAmp", "uB1", "uB2", "uB3", "uListen", "uThink", "uForm", "uDark", "uA", "uB", "uC", "uD"].forEach(
      (n) => (u[n] = U(n)),
    );
    // siri-crimson form, dark glass — the navbar sits on a glass pill.
    gl.uniform3f(u.uA, 1.0, 0.45, 0.55);
    gl.uniform3f(u.uB, 0.9, 0.3, 0.5);
    gl.uniform3f(u.uC, 1.0, 0.65, 0.4);
    gl.uniform3f(u.uD, 0.8, 0.2, 0.45);

    let raf = 0;
    let start = 0;
    const frame = (ms: number) => {
      if (!start) start = ms;
      const t = (ms - start) * 0.001;
      const amp = 0.35 + 0.3 * Math.abs(Math.sin(t * 0.9));
      gl.viewport(0, 0, cv.width, cv.height);
      gl.uniform2f(u.uRes, cv.width, cv.height);
      gl.uniform1f(u.uTime, t);
      gl.uniform1f(u.uAmp, amp);
      gl.uniform1f(u.uB1, 0.4 + 0.4 * Math.sin(t * 1.3));
      gl.uniform1f(u.uB2, 0.4 + 0.4 * Math.sin(t * 1.7 + 1.0));
      gl.uniform1f(u.uB3, 0.4 + 0.4 * Math.sin(t * 2.1 + 2.0));
      gl.uniform1f(u.uListen, 0);
      gl.uniform1f(u.uThink, think);
      gl.uniform1f(u.uForm, 4);
      gl.uniform1f(u.uDark, 1);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };
    const startLoop = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const stopLoop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };
    const onVis = () => (document.hidden ? stopLoop() : startLoop());
    document.addEventListener("visibilitychange", onVis);
    startLoop();

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopLoop();
      // NOTE: deliberately do NOT loseContext() here. Under React StrictMode the
      // effect mounts→unmounts→remounts; losing the context makes the remount's
      // getContext() return the now-lost context (shaders can't compile → the orb
      // falls back to the flat disc). There's only one orb (the nav), so letting GC
      // reclaim the context is fine.
    };
  }, [res, think]);

  return <canvas ref={ref} aria-hidden className={cn("block h-full w-full", className)} />;
}
